import type { ComponentProps } from "react";
import { MediaTrayView } from "@softmax/product-ux/editor";
import { useEffect, useRef, useState } from "react";

import type { HeroMediaItem } from "../store-page/types";
import { storePageApi } from "../store-page/client";
import { useEditMode } from "../use-edit-mode";
import {
  altTextMaxCharacters,
  maxMediaItems,
} from "../store-page/content-limits";
import { isAllowedVideoUrl } from "../store-page/link-validation";

function reorder(items: HeroMediaItem[]): HeroMediaItem[] {
  return items.map((item, order) => ({ ...item, order }));
}

export function useMediaTray(): ComponentProps<typeof MediaTrayView> {
  const { slug, editor } = useEditMode();
  const sorted = editor.content.hero_media.toSorted(
    (a, b) => a.order - b.order,
  );
  const [uploadingImage, setUploadingImage] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [pendingPoster, setPendingPoster] = useState<string | null>(null);
  const [videoUrlError, setVideoUrlError] = useState<string | null>(null);
  const [uploadFailure, setUploadFailure] = useState<{
    kind: "image" | "poster";
    file: File;
    message: string;
  } | null>(null);
  const active = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  function setItems(next: HeroMediaItem[]) {
    editor.updateContent({ hero_media: reorder(next) });
  }

  function upload({ kind, file }: { kind: "image" | "poster"; file: File }) {
    const setUploading =
      kind === "image" ? setUploadingImage : setUploadingPoster;
    setUploading(true);
    setUploadFailure(null);
    void storePageApi
      .uploadImage({ slug, file })
      .then(
        (uploaded) => {
          if (!active.current) return;

          if (kind === "poster") {
            setPendingPoster(uploaded.full);
          } else {
            editor.updateContent((content) => ({
              hero_media: reorder([
                ...content.hero_media.toSorted((a, b) => a.order - b.order),
                {
                  kind: "image",
                  src: uploaded.full,
                  poster: null,
                  alt: "",
                  order: 0,
                },
              ]),
            }));
          }
        },
        (error: unknown) => {
          if (active.current)
            setUploadFailure({
              kind,
              file,
              message: error instanceof Error ? error.message : String(error),
            });
        },
      )
      .finally(() => {
        if (active.current) setUploading(false);
      });
  }

  function addVideo() {
    if (!isAllowedVideoUrl(videoUrl)) {
      setVideoUrlError(
        "Unsupported video URL. Use YouTube, Vimeo, or a direct .mp4/.webm/.mov link.",
      );
      return;
    }

    if (!pendingPoster) {
      setVideoUrlError("Upload a poster image first.");
      return;
    }

    setItems([
      ...sorted,
      {
        kind: "video_url",
        src: videoUrl,
        poster: pendingPoster,
        alt: "",
        order: 0,
      },
    ]);
    setVideoUrl("");
    setPendingPoster(null);
    setVideoUrlError(null);
  }

  return {
    items: sorted.map((item) => ({ ...item, poster: item.poster ?? null })),
    disabled: !editor.loaded || editor.mutating,
    maxItems: maxMediaItems,
    altTextMax: altTextMaxCharacters,
    uploadingImage: uploadingImage,
    uploadingPoster: uploadingPoster,
    videoUrl: videoUrl,
    posterReady: pendingPoster !== null,
    videoError: videoUrlError,
    uploadError: uploadFailure?.message ?? null,
    onRetryUpload: () => {
      if (uploadFailure)
        upload({ kind: uploadFailure.kind, file: uploadFailure.file });
    },
    showLiveReplay: editor.content.show_live_replay,
    onImageFile: (file) => upload({ kind: "image", file }),
    onPosterFile: (file) => upload({ kind: "poster", file }),
    onVideoUrlChange: setVideoUrl,
    onAddVideo: addVideo,
    onAltChange: ({ index, value: alt }) =>
      setItems(
        sorted.map((item, i) => (i === index ? { ...item, alt } : item)),
      ),
    onRemove: (index) => setItems(sorted.filter((_, i) => i !== index)),
    onReorder: ({ from, to }) => {
      const next = sorted.slice();
      const moved = next.splice(from, 1);
      next.splice(to, 0, ...moved);
      setItems(next);
    },
    onLiveReplayChange: (show_live_replay) =>
      editor.updateContent({ show_live_replay }),
  };
}
